import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Close, Send, Support } from '@mui/icons-material';
import './SupportForm.css';

const SupportForm = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    category: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const categories = [
    { value: 'account', label: 'Compte et connexion' },
    { value: 'missions', label: 'Missions et postulations' },
    { value: 'payments', label: 'Paiements et facturation' },
    { value: 'technical', label: 'Problème technique' },
    { value: 'other', label: 'Autre' }
  ];

  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Le nom doit contenir au moins 2 caractères';
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }
    
    // Subject validation
    if (!formData.subject.trim()) {
      newErrors.subject = 'Le sujet est requis';
    } else if (formData.subject.trim().length < 5) {
      newErrors.subject = 'Le sujet doit contenir au moins 5 caractères';
    }
    
    // Category validation
    if (!formData.category) {
      newErrors.category = 'Veuillez sélectionner une catégorie';
    }
    
    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Le message est requis';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Le message doit contenir au moins 10 caractères';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Mock API call - simulate network delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock success response
      console.log('Support form submitted:', formData);
      
      setSubmitStatus('success');
      
      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          subject: '',
          category: '',
          message: ''
        });
        setSubmitStatus(null);
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        name: '',
        email: '',
        subject: '',
        category: '',
        message: ''
      });
      setErrors({});
      setSubmitStatus(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className="support-form-dialog"
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pb: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Support sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="h2">
            Contacter le support
          </Typography>
        </Box>
        <IconButton 
          onClick={handleClose} 
          disabled={isSubmitting}
          aria-label="Fermer"
        >
          <Close />
        </IconButton>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 2 }}>
          {submitStatus === 'success' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Votre message a été envoyé avec succès ! Notre équipe vous répondra dans les plus brefs délais.
            </Alert>
          )}
          
          {submitStatus === 'error' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Une erreur est survenue lors de l'envoi. Veuillez réessayer.
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nom complet"
              value={formData.name}
              onChange={handleInputChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              required
              fullWidth
              disabled={isSubmitting}
              autoComplete="name"
            />
            
            <TextField
              label="Adresse email"
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              required
              fullWidth
              disabled={isSubmitting}
              autoComplete="email"
            />
            
            <FormControl fullWidth error={!!errors.category} disabled={isSubmitting}>
              <InputLabel>Catégorie</InputLabel>
              <Select
                value={formData.category}
                onChange={handleInputChange('category')}
                label="Catégorie"
                required
              >
                {categories.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.category && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.category}
                </Typography>
              )}
            </FormControl>
            
            <TextField
              label="Sujet"
              value={formData.subject}
              onChange={handleInputChange('subject')}
              error={!!errors.subject}
              helperText={errors.subject}
              required
              fullWidth
              disabled={isSubmitting}
            />
            
            <TextField
              label="Message"
              value={formData.message}
              onChange={handleInputChange('message')}
              error={!!errors.message}
              helperText={errors.message}
              required
              fullWidth
              multiline
              rows={4}
              disabled={isSubmitting}
              placeholder="Décrivez votre problème ou votre question en détail..."
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={handleClose} 
            disabled={isSubmitting}
            color="inherit"
          >
            Annuler
          </Button>
          <Button 
            type="submit"
            variant="contained"
            disabled={isSubmitting || submitStatus === 'success'}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Send />
              )
            }
            sx={{ minWidth: 120 }}
          >
            {isSubmitting ? 'Envoi...' : 'Envoyer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SupportForm;